<script lang="ts" setup>
import { ref, watch, computed } from "vue"

import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"

import { createConventionMutation } from "@/lib/utils.ts"

import { Convention } from "@/models/convention.ts"
import {
  createConventionUserSchema,
  updateConventionUserSchema,
  createConventionUser,
  updateConventionUser,
  ConventionUser,
  CreateConventionUser,
  UpdateConventionUser
} from "@/models/convention_user.ts"

import { AutoForm, AutoFormField, Config } from "@/components/ui/auto-form"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog"
import { DependencyType } from "@/components/ui/auto-form/interface"
import { ZodObjectOrWrapped } from "@/components/ui/auto-form/utils.ts"

import AutoFormEnum from "@/components/ui-custom/auto-form-enum/AutoFormEnum.vue"

const props = defineProps<{
  convention: Convention
  conventionUser?: ConventionUser
  role?: string
  alert?: Boolean
}>()

const { t } = useI18n()

const emits = defineEmits(["success"])

const isOpen = ref(false)

const creationMode = computed(() => !props.conventionUser)

const createConventionUserMutation = createConventionMutation({
  mutationFn: createConventionUser,
  successDescription: t("conventions.toasts.user_creation"),
  errorDescription: t("conventions.toasts.user_creation_error"),
  successCallback: (conventionUser: ConventionUser) => {
    isOpen.value = false

    emits("success", conventionUser)
  }
})

const updateConventionUserMutation = createConventionMutation({
  mutationFn: updateConventionUser,
  successDescription: t("conventions.toasts.user_edit"),
  errorDescription: t("conventions.toasts.user_edit_error"),
  successCallback: (conventionUser: ConventionUser) => {
    isOpen.value = false

    emits("success", conventionUser)
  }
})

const getDefaultValues = (): CreateConventionUser | UpdateConventionUser => {
  if (props.conventionUser) {
    return {
      id: props.conventionUser.id,
      name: props.conventionUser.resource?.name || "",
      surname: props.conventionUser.resource?.surname || "",
      username: props.conventionUser.username || "",
      telephone: props.conventionUser.resource?.telephone || "",
      role: props.role
    } as unknown as UpdateConventionUser
  } else {
    return {} as CreateConventionUser
  }
}

const schema = computed(() => (props.conventionUser ? updateConventionUserSchema : createConventionUserSchema))
const formSchema = computed(() => toTypedSchema(schema.value))
const form = useForm({
  validationSchema: formSchema.value,
  initialValues: getDefaultValues()
})

const fieldConfig: Config<CreateConventionUser | UpdateConventionUser> = {
  name: {
    label: t("conventions.form.name")
  },
  surname: {
    label: t("conventions.form.surname")
  },
  username: {
    label: t("conventions.form.email")
  },
  telephone: {
    label: t("conventions.form.phone_number")
  },
  role: {
    label: t("conventions.form.role")
  }
}

const conventionUserLabel = computed(() => props.conventionUser?.username)
const dialogTitle = computed(() =>
  !creationMode.value
    ? `${t("conventions.form.edit_title")}: ` + conventionUserLabel.value
    : t("conventions.form.new_title")
)
const dialogDescription = computed(() =>
  !creationMode.value ? t("conventions.form.edit_description") : t("conventions.form.new_description")
)

const disableRoleField = computed(() => !creationMode.value && props.role == "admin_convention_users")

watch(isOpen, (newValue, _) => {
  if (newValue) {
    form.resetForm({
      values: getDefaultValues()
    })
  }
})

const onSubmit = form.handleSubmit(async (values: CreateConventionUser | UpdateConventionUser) => {
  await schema.value.parseAsync(values)
  !creationMode.value
    ? onUpdateConventionUser(values as UpdateConventionUser)
    : onCreateConventionUser(values as CreateConventionUser)
})

const onUpdateConventionUser = (values: UpdateConventionUser) => {
  const mutationValues = { ...values, convention_id: props.convention.id }
  updateConventionUserMutation.mutate(mutationValues)
}

const onCreateConventionUser = (values: CreateConventionUser) => {
  const mutationValues = { ...values, convention_id: props.convention.id }
  createConventionUserMutation.mutate(mutationValues)
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot></slot>
    </DialogTrigger>
    <DialogContent @interact-outside.prevent>
      <DialogHeader>
        <DialogTitle>{{ dialogTitle }}</DialogTitle>
        <DialogDescription>{{ dialogDescription }}</DialogDescription>
      </DialogHeader>
      <AutoForm
        :schema="schema as unknown as ZodObjectOrWrapped"
        :form="form"
        :fieldConfig="fieldConfig"
        :dependencies="[
          {
            sourceField: 'username',
            type: DependencyType.HIDES,
            targetField: 'id',
            when: (_username: string) => true
          }
        ]"
        class="flex flex-wrap gap-4 overflow-auto p-1">
        <template #id="slotProps" v-if="false">
          <AutoFormField v-bind="slotProps" class="w-full" />
        </template>

        <template #name="slotProps">
          <AutoFormField v-bind="slotProps" required class="flex-1" />
        </template>

        <template #surname="slotProps">
          <AutoFormField v-bind="slotProps" required class="flex-1" />
        </template>

        <template #username="slotProps">
          <AutoFormField v-bind="slotProps" required class="w-full" :disabled="!!conventionUser" />
        </template>

        <template #telephone="slotProps">
          <AutoFormField v-bind="slotProps" class="w-full" />
        </template>

        <template #role="slotProps">
          <div class="flex-1">
            <AutoFormEnum
              v-bind="slotProps"
              required
              :disabled="disableRoleField"
              :options="[
                {
                  value: 'admin_convention_users',
                  label: $t('conventions.roles.admin_convention_users').toUpperCase(),
                  disable: true
                },
                { value: 'super_reader', label: $t('conventions.roles.super_reader').toUpperCase(), disable: false },
                { value: 'reader', label: $t('conventions.roles.reader').toUpperCase(), disable: false }
              ]" />
          </div>
        </template>
      </AutoForm>
      <DialogFooter>
        <div class="flex items-center justify-end gap-2">
          <DialogClose as-child>
            <Button type="button" variant="secondary">{{ $t("commons.cancel") }}</Button>
          </DialogClose>
          <Button @click="onSubmit">{{ $t("commons.save") }}</Button>
        </div>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
